import { Gym } from '../gym';
import { BaseEquipment, Equipment, EquipmentCombinations } from '../workout/equipment';
import { IOrganisationConfig } from './organisation';

export interface IEnabledFeatures {
  organisationConfig?: IOrganisationConfig;
  workout: IWorkoutFeatures;
}

interface IWorkoutFeatures {
  home: IHomeFeatures;
  homeGym: Gym;
  allowedStudioGyms: Gym[];
}

interface IHomeFeatures {
  allowedEquipment: Equipment[];
}

export class EnabledFeatures {
  public static canSelectEquipment = (enabledFeatures: IEnabledFeatures) => {
    const disableEquip = enabledFeatures.organisationConfig?.workoutConfig?.disableHomeEquipmentForUsersWithoutAccess;
    const userAllowedEquipment = (enabledFeatures.workout.home.allowedEquipment.length > 1)
      && !enabledFeatures.workout.home.allowedEquipment.every(
        (equipment) => equipment === Equipment.Bodyweight,
      );
    return !disableEquip || userAllowedEquipment;
  };

  public static canUseBenchWithBands = (
    enabledFeatures: IEnabledFeatures,
  ) => BaseEquipment.containsHomeBench(enabledFeatures.workout.home.allowedEquipment)
    && BaseEquipment.containsAllHomeBands(enabledFeatures.workout.home.allowedEquipment);

  public static canUseBenchWithDumbells = (
    enabledFeatures: IEnabledFeatures,
  ) => BaseEquipment.containsHomeBench(enabledFeatures.workout.home.allowedEquipment)
    && BaseEquipment.containsHomeDumbbells(enabledFeatures.workout.home.allowedEquipment);

  public static hasAccessToAStudioGym = (
    enabledFeatures: IEnabledFeatures,
  ) => enabledFeatures.workout.allowedStudioGyms.length > 0;

  public static getEquipmentListFromEquipmentCombination = (enabledFeatures: IEnabledFeatures):
  Equipment[] => {
    const equipmentCombination = BaseEquipment.getEquipmentCombination(
      enabledFeatures.workout.home.allowedEquipment,
    );

    return BaseEquipment.EquipmentCombinationList[equipmentCombination] || [Equipment.Bodyweight];
  };

  public static calculateEquipmentSettings(enabledFeatures: IEnabledFeatures, defaultEquipment?: Equipment[]) {
    return defaultEquipment
      ? EnabledFeatures.calculateEquipmentFromDefaultEquipment(
        enabledFeatures,
        defaultEquipment,
      )
      : EnabledFeatures.getEquipmentListFromEquipmentCombination(
        enabledFeatures,
      );
  }

  public static calculateEquipmentFromDefaultEquipment(
    enabledFeatures: IEnabledFeatures,
    defaultEquipment: Equipment[],
  ) {
    const hasBenchWithDumbbellsOrBands = () => {
      const equipmentCombination = BaseEquipment.getEquipmentCombination(defaultEquipment);
      return (equipmentCombination === EquipmentCombinations.BENCH_BANDS
        || equipmentCombination === EquipmentCombinations.BENCH_DUMBBELLS
        || equipmentCombination === EquipmentCombinations.BENCH_BANDS_DUMBBELLS);
    };
    const hasBenchWithBands = () => {
      const equipmentCombination = BaseEquipment.getEquipmentCombination(defaultEquipment);
      return (equipmentCombination === EquipmentCombinations.BENCH_BANDS);
    };

    const hasDumbbells = () => {
      const equipmentCombination = BaseEquipment.getEquipmentCombination(defaultEquipment);
      return (equipmentCombination === EquipmentCombinations.DUMBBELLS);
    };

    const hasBenchWithDumbbells = () => {
      const equipmentCombination = BaseEquipment.getEquipmentCombination(defaultEquipment);
      return (equipmentCombination === EquipmentCombinations.BENCH_HUB_DUMBBELLS);
    };

    const hasBodyweightOnly = () => defaultEquipment.length === 1 && defaultEquipment[0] === Equipment.Bodyweight;

    const calculateDefaultEquipmentWithDumbbellsOrBands = () => {
      const hasEquipmentAsDefault = hasBenchWithDumbbellsOrBands() || hasDumbbells();

      if (hasEquipmentAsDefault) {
        return defaultEquipment;
      }
      return EnabledFeatures.getEquipmentListFromEquipmentCombination(enabledFeatures);
    };
    if (hasBenchWithDumbbells()) {
      const equipmentCombonation = BaseEquipment.getEquipmentCombination(defaultEquipment);
      return BaseEquipment.EquipmentCombinationList[equipmentCombonation] || [Equipment.Bodyweight];
    }
    if (hasBenchWithDumbbellsOrBands() || hasBenchWithBands() || hasDumbbells()) {
      return calculateDefaultEquipmentWithDumbbellsOrBands();
    }

    if (hasBodyweightOnly()) {
      return defaultEquipment;
    }

    return EnabledFeatures.getEquipmentListFromEquipmentCombination(enabledFeatures);
  }

  public static guessTimezone(enabledFeatures: IEnabledFeatures): string {
    if (enabledFeatures.workout.allowedStudioGyms.length > 0) {
      return enabledFeatures.workout.allowedStudioGyms[0].timezone;
    }
    return enabledFeatures.workout.homeGym.timezone;
  }

  public static getLandingPageUrl(enabledFeatures: IEnabledFeatures): string {
    function getGym(): Gym {
      if (enabledFeatures.workout.allowedStudioGyms.length > 0) {
        return enabledFeatures.workout.allowedStudioGyms[0];
      }
      return enabledFeatures.workout.homeGym;
    }

    return getGym().landingPage || 'https://fitillion.com';
  }
}
